/**
 * Core-100 R4 wave 3 (CORE-AY D) — the ONE way the browser asks `customer-search`, and the one way it reads the answer.
 *
 * Three surfaces called the function, each with its own request and its own reading of the reply:
 *   - TopBar's universal search (`runUniversalSearch`) — the contract: classify with `detectSearchType`, a failed
 *     source is said as failed ("could not be searched just now"), never as "nothing found", and the scope is stated;
 *   - `CustomerSearchBar` (Customers page, Create Ticket) through `useCustomerSearch`;
 *   - `useCustomers(searchQuery)` in `useCustomer.ts`.
 * The last two built their own request and treated a reply with an `error` field, or without a `results` array, as an
 * EMPTY result — and the search bar then showed nothing, so "the search failed" read as "no such customer".
 *
 * All three now build the request here and read the reply here: one minimum length, one classifier, one failure rule,
 * one scope sentence (the same one the universal search shows).
 */
import { detectSearchType, type CustomerSearchType } from './detectSearchType';
import type { CustomerSearchResult } from '@/lib/customers/types';

/** Fewer characters than this are not sent (the function would only enumerate). */
export const CUSTOMER_SEARCH_MIN_CHARS = 2;

/** What a customer/vehicle search covers — shown with its results (and with an empty result). */
export const CUSTOMER_SEARCH_SCOPE =
  'Customers and vehicles by name (two characters match the start of a name, three or more anywhere), phone, email, VIN (whole or its last 8) or plate (whole or its last 3 or more characters); protected numbers are shown masked.';

/** Said when the search could not run — never "no results". */
export const CUSTOMER_SEARCH_FAILED = 'Customers and vehicles could not be searched just now.';

/**
 * Core-100 R4 CORE-AY (Rel): said when the server REFUSED the search because this person searched too often (429,
 * the per-user, per-shop bound) — never "no results", never the generic failure.
 */
export const CUSTOMER_SEARCH_RATE_LIMITED = 'Too many searches — try again shortly.';

export interface CustomerSearchRequest {
  shop_id: string;
  query: string;
  type: CustomerSearchType;
  limit: number;
}

/** The request body, or null when the term is too short to send. */
export function customerSearchRequest(shopId: string, term: string, limit: number): CustomerSearchRequest | null {
  const query = (term ?? '').trim();
  if (!shopId || query.length < CUSTOMER_SEARCH_MIN_CHARS) return null;
  return { shop_id: shopId, query, type: detectSearchType(query), limit };
}

/**
 * `ok` false = the search FAILED (`reason` says so, `results` is empty and must not be shown as "none found").
 * `rateLimited` true = the failure is the server's search bound (429): `reason` is CUSTOMER_SEARCH_RATE_LIMITED.
 */
export interface CustomerSearchOutcome {
  ok: boolean;
  results: CustomerSearchResult[];
  reason: string | null;
  rateLimited?: boolean;
}
const failed = (): CustomerSearchOutcome => ({ ok: false, results: [], reason: CUSTOMER_SEARCH_FAILED });
const rateLimited = (): CustomerSearchOutcome => ({ ok: false, results: [], reason: CUSTOMER_SEARCH_RATE_LIMITED, rateLimited: true });

/** True when the reply is the search bound's refusal: HTTP 429 (supabase-js puts the Response on `error.context`). */
function isRateLimitRefusal(data: unknown, error: unknown): boolean {
  const status = (error as { context?: { status?: unknown } } | null | undefined)?.context?.status;
  if (status === 429) return true;
  return (data as { code?: unknown } | null | undefined)?.code === 'SEARCH_RATE_LIMITED';
}

/** A transport error, an `error` in the body, or a body without a results array is a FAILURE — never an empty list. */
export function readCustomerSearchResponse(data: unknown, error: unknown): CustomerSearchOutcome {
  if (isRateLimitRefusal(data, error)) return rateLimited();
  if (error) return failed();
  const body = data as { results?: unknown; error?: unknown } | null | undefined;
  if (!body || body.error || !Array.isArray(body.results)) return failed();
  return { ok: true, results: body.results as CustomerSearchResult[], reason: null };
}

/** The minimal client surface used (always called as `client.functions.invoke(...)`, never detached). */
interface FunctionsClient {
  functions: { invoke: (name: string, opts: { body: unknown }) => Promise<{ data: unknown; error: unknown }> };
}

/** Run one search through the contract. A term too short to send is an empty, successful search. */
export async function runCustomerSearch(client: FunctionsClient, shopId: string, term: string, limit: number): Promise<CustomerSearchOutcome> {
  const body = customerSearchRequest(shopId, term, limit);
  if (!body) return { ok: true, results: [], reason: null };
  try {
    const { data, error } = await client.functions.invoke('customer-search', { body });
    return readCustomerSearchResponse(data, error);
  } catch {
    return failed();
  }
}
